import {
  JOURNEY_ATTENDANCE_STATUS,
  REALTIME_AGGREGATE_STATUS,
  USER_ROW_ATTENDANCE_STATE,
  WORK_JOURNEY_REAL_TIME_TAB,
  type RealtimeAttendanceStatusCode,
  type WorkJourneyRealTimeTab,
} from '@src/packages/workJourney/constants';

import type { AttendanceKpis } from './attendanceStatus/types';

export type RealTimeKpiKey =
  | 'journeysInProgress'
  | 'presentUsers'
  | 'notMarkedUsers'
  | 'absentUsers'
  | 'requiresAction';

export interface RealTimeKpiFilter {
  key: RealTimeKpiKey;
  kpiField: keyof AttendanceKpis;
  labelId: string;
  tone?: 'default' | 'danger';
  tab: WorkJourneyRealTimeTab;
  statusCode: RealtimeAttendanceStatusCode;
}

export const REAL_TIME_KPI_FILTERS: readonly RealTimeKpiFilter[] = [
  {
    key: 'journeysInProgress',
    kpiField: 'journeysInProgress',
    labelId: 'workJourney.realTime.kpi.journeysInProgress',
    tab: WORK_JOURNEY_REAL_TIME_TAB.BY_JOURNEY,
    statusCode: JOURNEY_ATTENDANCE_STATUS.IN_PROGRESS,
  },
  {
    key: 'presentUsers',
    kpiField: 'presentUsers',
    labelId: 'workJourney.realTime.kpi.presentUsers',
    tab: WORK_JOURNEY_REAL_TIME_TAB.BY_USER,
    // Must match computeAttendanceKpis.presentUsers (ATTENDED + marked LATE).
    statusCode: REALTIME_AGGREGATE_STATUS.PRESENT,
  },
  {
    key: 'notMarkedUsers',
    kpiField: 'notMarkedUsers',
    labelId: 'workJourney.realTime.kpi.notMarkedUsers',
    tab: WORK_JOURNEY_REAL_TIME_TAB.BY_USER,
    statusCode: REALTIME_AGGREGATE_STATUS.UNMARKED,
  },
  {
    key: 'absentUsers',
    kpiField: 'absentUsers',
    labelId: 'workJourney.realTime.kpi.absentUsers',
    tab: WORK_JOURNEY_REAL_TIME_TAB.BY_USER,
    statusCode: USER_ROW_ATTENDANCE_STATE.ABSENT,
  },
  {
    key: 'requiresAction',
    kpiField: 'requiresAction',
    labelId: 'workJourney.realTime.kpi.requiresAction',
    tone: 'danger',
    tab: WORK_JOURNEY_REAL_TIME_TAB.BY_JOURNEY,
    statusCode: JOURNEY_ATTENDANCE_STATUS.BELOW_MINIMUM,
  },
];

export const resolveActiveKpiKey = ({
  activeTab,
  status,
}: {
  activeTab: WorkJourneyRealTimeTab;
  status: string;
}): RealTimeKpiKey | null =>
  REAL_TIME_KPI_FILTERS.find(
    filter => filter.tab === activeTab && filter.statusCode === status,
  )?.key ?? null;

export const resolveKpiFilterSelection = (
  activeKpiKey: RealTimeKpiKey | null,
  selectedKpiKey: RealTimeKpiKey,
): {
  tab: WorkJourneyRealTimeTab;
  statusCode: RealtimeAttendanceStatusCode | '';
} => {
  const selectedFilter = REAL_TIME_KPI_FILTERS.find(filter => filter.key === selectedKpiKey);
  if (!selectedFilter) {
    throw new Error(`Unknown Real-Time KPI filter: ${selectedKpiKey}`);
  }

  return {
    tab: selectedFilter.tab,
    statusCode: activeKpiKey === selectedKpiKey ? '' : selectedFilter.statusCode,
  };
};
